import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import moment from 'moment';

import {  getContact } from 'store/app/portal/actions';
import { updateBreadcrumb } from 'store/breadcrumb/actions';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { UserRole } from 'utils/enums';
import { isEmailAddressValid } from 'utils/helpers';

import GeneralLayout from 'containers/Templates/Layout/GeneralLayout';
import GuestLayout from 'containers/Templates/Layout/GuestLayout';

import LandingPage from 'containers/Pages/Common/LandingPage';
import LogoutPage from 'containers/Pages/Common/LogoutPage';
import LoginPage from 'containers/Pages/Common/LoginPage';
import ResetPasswordPage from 'containers/Pages/User/UserProfile/ResetPassword';

import Breadcrumbs from 'components/Common/Breadcrumbs';

import { routes } from './Routes';
import { RoutesProps } from './Proptypes';
import packageJson from '../../../package.json';

const Routes = (props: RoutesProps) => {

  const [version, setVersion] = useState('');
  const [portalContact, setPortalContact] = useState('');
  const IS_SIX_MONTH_DIFF = moment().diff(moment(sessionStorage.getItem('modified_date')), 'months', true) > 6 ? true : false;
  const EMAIL = isEmailAddressValid(sessionStorage.getItem('email'));

  useEffect(() => {
    getVersion();
  }, [])

  const getVersion = () => {
    setVersion(packageJson.version);
  };

  const getContact = () => {
    return props.getContact()
      .then((response: any) => {
        setPortalContact(response.RmiPhone);
      })
      .catch((err: ErrorResponse) => {
        if (err?.response?.status !== 401) notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const CommonRoutes = () => {
    return (
      [
        <Route key="/" exact path="/" component={LandingPage}>
          <Redirect to="/login" />
        </Route>,
        <Route key="/logout" exact path="/logout" component={LogoutPage} />,
      ]
    );
  };

  const GuestRoutes = (location: any) => {
    return (

      <Switch location={location}>

        <Route key="/login" exact path="/login" component={LoginPage} />
        {CommonRoutes()}
        <Route key="/reset-password" exact path="/reset-password" component={ResetPasswordPage} />
        <Route key="error" exact component={LoginPage} >
          <Redirect to="/login" />
        </Route>

      </Switch>
    );
  };

  const UserRoutes = (user_role: string | null, location: any) => {
    if (portalContact === '') getContact();

    return (
      <Switch location={location}>

        {routes.map(({ pathname, Component }, key) => {
          const authEnabled = sessionStorage.getItem('auth_enabled');
          const claimEnabled = sessionStorage.getItem('claim_enabled');

          if ((pathname.includes('/claims/') && !claimEnabled) ||
            (pathname.includes('/auth/') && !authEnabled))
            return null;

          return (
            <Route
              path={pathname}
              key={key}
              render={(props: any) => {
                return (
                  <>
                    <Component {...props} />
                  </>
                );
              }}
            />
          );
        })}

        {sessionStorage.getItem('user_role') === UserRole.THIRD_PARTY &&
          <>
            <Redirect exact from="/login" to="/claims/search" />
            <Redirect exact from="/" to="/claims/search" />

            <Redirect key="/login" exact from="/login" to="/claims/search" />
            <Redirect key="/" exact from="/" to="/claims/search" />
          </>
        }

        {sessionStorage.getItem('user_role') !== UserRole.THIRD_PARTY && !IS_SIX_MONTH_DIFF && EMAIL &&
          <>
            <Redirect exact from="/login" to="/quality/overview" />
            <Redirect exact from="/" to="/quality/overview" />

            <Redirect key="/login" exact from="/login" to="/quality/overview" />
            <Redirect key="/" exact from="/" to="/quality/overview" />
          </>
        }

        {sessionStorage.getItem('user_role') !== UserRole.THIRD_PARTY && (IS_SIX_MONTH_DIFF || !EMAIL) ?
          <>
            <Redirect exact from="/login" to="/account/profile" />
            <Redirect exact from="/" to="/account/profile" />

            <Redirect key="/login" exact from="/login" to="/account/profile" />
            <Redirect key="/" exact from="/" to="/account/profile" />
          </> : <></>
        }
        {CommonRoutes()}
        <Route key="error" exact component={LoginPage} />
      </Switch>
    );
  };

  const { location, history, isAuthenticated, loggedIn, user_role, user, acceptedTerms, ...rest } = props;

  let layout;
  if (loggedIn) {
    layout =  <GeneralLayout
                {...props}
                location={location}
                user={user}
                isAuthenticated={loggedIn}
                user_role={user_role}
                history={history}
                version={version}
                portalContact={portalContact}
              >
                {sessionStorage.getItem('user_role') !== UserRole.THIRD_PARTY &&
                  <Breadcrumbs
                    routes={routes}
                    currentPath={location.pathname}
                    breadcrumb={props.breadcrumb}
                    updateBreadcrumb={props.updateBreadcrumb}
                  />
                }

                {UserRoutes(user_role, location)}
              </GeneralLayout>
  }
  else {
    layout = <GuestLayout location={location} history={history} version={version} >
              {sessionStorage.getItem('user_role') !== UserRole.THIRD_PARTY &&
                <Breadcrumbs
                  routes={routes}
                  currentPath={location.pathname}
                  breadcrumb={props.breadcrumb}
                  updateBreadcrumb={props.updateBreadcrumb}
                />
              }
                {GuestRoutes(location)}
              </GuestLayout>
  }

  return (

      <TransitionGroup className="transition-group">
        <CSSTransition
          key={location.pathname}
          timeout={{ enter: 300, exit: 300 }}
          classNames="fade1"
        >
          <section className="html-v1 route-section">
            {layout}
          </section>
        </CSSTransition>
      </TransitionGroup>
  );
}

const mapStateToProps = (state: RootStateOrAny) => ({
  isAuthenticated: !!(state.rootReducer.auth?.user && state.rootReducer.auth?.access_token),
  user_role: (state.rootReducer.auth?.user?.user_role) || '',
  user: state.rootReducer.auth?.user,
  loggedIn: state.rootReducer.auth?.loggedIn,
  isFetchingVersion: state.rootReducer.appPortal.isFetchingVersion,
  acceptedTerms: state.rootReducer.auth?.acceptedTerms,
  breadcrumb: state.persistedReducer.breadcrumb
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    updateBreadcrumb,
    getContact,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(Routes);